import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';
import { useProcessingBridge } from './useProcessingBridge';

interface BillQueueItem {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
  upload_status: 'uploaded' | 'processing' | 'completed' | 'failed';
  processing_status: 'pending' | 'processing' | 'completed' | 'failed' | 'review_required';
  assignment_status: 'unassigned' | 'auto_assigned' | 'manually_assigned' | 'rejected';
  extracted_data?: any;
  ai_confidence?: number;
  supplier_name?: string;
  bill_date?: string;
  amount?: number;
  bill_number?: string;
  service_type?: string;
  assigned_to_account_id?: string;
  assigned_by?: string;
  assigned_at?: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  processing_error?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
  manual_review_required?: boolean;
  priority?: number;
  // Enhanced fields for new processing system
  customer_name_raw?: string;
  customer_name_normalized?: string;
  account_number_raw?: string;
  account_number_normalized?: string;
  matching_category?: 'NEW-NEW' | 'OLD-NEW' | 'OLD-OLD';
  customer_confidence?: number;
  account_confidence?: number;
  auto_assigned?: boolean;
  matching_details?: any;
  validation_errors?: string[];
  requires_review?: boolean;
  // Intake-specific fields
  is_readable?: boolean;
  is_duplicate?: boolean;
  dup_of_queue_id?: string;
  source_type?: string;
  source_ref?: string;
  source_address?: string;
  content_hash?: string;
  unlock_needed?: boolean;
  stage_entered_at?: string;
  sla_due_at?: string;
  flags?: string[];
  // Assignment tracking fields
  claimed_by?: string;
  claimed_at?: string;
  reviewed_at?: string;
  review_duration_seconds?: number;
}

interface CreateQueueItemData {
  file_name: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
}

interface UpdateQueueItemData {
  processing_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'review_required';
  assignment_status?: 'unassigned' | 'auto_assigned' | 'manually_assigned' | 'rejected';
  extracted_data?: any;
  ai_confidence?: number;
  supplier_name?: string;
  bill_date?: string;
  amount?: number;
  bill_number?: string;
  service_type?: string;
  assigned_to_account_id?: string;
  processing_error?: string;
  notes?: string;
  manual_review_required?: boolean;
  // Intake-specific updates
  is_readable?: boolean;
  is_duplicate?: boolean;
  dup_of_queue_id?: string;
  source_type?: string;
  source_ref?: string;
  stage_entered_at?: string;
  sla_due_at?: string;
  flags?: string[];
}


interface AssignToBillData {
  assigned_to_account_id: string;
  notes?: string;
}

export const useBillQueue = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Import processing bridge methods
  const { processQueueItem, createBillFromQueue } = useProcessingBridge();

  // Fetch queue items
  const queueQuery = useQuery({
    queryKey: ['bill-queue', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BillQueueItem[];
    },
    enabled: !!user?.id,
  });

  // Create queue item mutation
  const createQueueItemMutation = useMutation({
    mutationFn: async (data: CreateQueueItemData) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data: result, error } = await supabase
        .from('bill_upload_queue')
        .insert({
          user_id: user.id,
          ...data,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
      toast({
        title: "File queued successfully",
        description: "Your file has been added to the processing queue.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to queue file",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update queue item mutation
  const updateQueueItemMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateQueueItemData }) => {
      const { data: result, error } = await supabase
        .from('bill_upload_queue')
        .update(data)
        .eq('id', id)
        .eq('user_id', user?.id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update queue item",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Assign to bill mutation
  const assignToBillMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AssignToBillData }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data: result, error } = await supabase
        .from('bill_upload_queue')
        .update({
          assigned_to_account_id: data.assigned_to_account_id,
          assigned_by: user.id,
          assigned_at: new Date().toISOString(),
          assignment_status: 'manually_assigned',
          notes: data.notes,
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
      toast({
        title: "Assignment successful",
        description: "The bill has been assigned to the selected account.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Assignment failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete queue item mutation
  const deleteQueueItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bill_upload_queue')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
      toast({
        title: "Item removed",
        description: "The queue item has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to remove item",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Fetch bills with stage filtering for proper Bill Journey integration
  const billsQuery = useQuery({
    queryKey: ['bills-by-stage', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('bills')
        .select('*, bill_upload_queue(*)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Enhanced helper functions to filter by bills.stage column
  const intakeItems = billsQuery.data?.filter(
    bill => bill.stage === 'intake'
  ) || [];

  const dataExtractionItems = billsQuery.data?.filter(
    bill => bill.stage === 'extraction'
  ) || [];

  const processingItems = billsQuery.data?.filter(
    bill => bill.stage === 'processing'
  ) || [];

  const manualReviewItems = billsQuery.data?.filter(
    bill => bill.stage === 'manual_review'
  ) || [];

  const validationItems = billsQuery.data?.filter(
    bill => bill.stage === 'validation'
  ) || [];

  const approvalItems = billsQuery.data?.filter(
    bill => bill.stage === 'approval'
  ) || [];

  const paymentReadyItems = billsQuery.data?.filter(
    bill => bill.stage === 'payment_ready'
  ) || [];

  const archiveItems = billsQuery.data?.filter(
    bill => bill.stage === 'archive'
  ) || [];

  // Legacy filters for backward compatibility
  const pendingItems = [...intakeItems, ...dataExtractionItems];
  const unassignedItems = processingItems;
  const assignedItems = [...validationItems, ...approvalItems, ...paymentReadyItems];
  const errorItems = queueQuery.data?.filter(
    item => item.processing_status === 'failed'
  ) || [];

  return {
    // Data
    queueItems: queueQuery.data || [],
    // 8-Queue System
    intakeItems,
    dataExtractionItems,
    processingItems,
    manualReviewItems,
    validationItems,
    approvalItems,
    paymentReadyItems,
    archiveItems,
    // Legacy compatibility
    pendingItems,
    unassignedItems,
    assignedItems,
    errorItems,
    
    // Loading states
    isLoading: queueQuery.isLoading,
    isError: queueQuery.isError,
    error: queueQuery.error,
    
    // Mutation states
    isCreating: createQueueItemMutation.isPending,
    isUpdating: updateQueueItemMutation.isPending,
    isAssigning: assignToBillMutation.isPending,
    isDeleting: deleteQueueItemMutation.isPending,
    
    // Actions
    createQueueItem: createQueueItemMutation.mutateAsync,
    updateQueueItem: updateQueueItemMutation.mutateAsync,
    assignToBill: assignToBillMutation.mutateAsync,
    deleteQueueItem: deleteQueueItemMutation.mutate,
    
    // Processing bridge methods
    processQueueItem,
    createBillFromQueue,
  };
};